/**
 * @fileoverview Pendo tracking hook for Bulk Actions feature.
 * Centralizes all bulk action tracking with standardized additional_info formatting.
 */

import { useCallback, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import {
  useStandardizedTracking,
  EventNames,
  ContentTypes,
  FeatureGroups,
  PageKeys,
  BulkActionContentKeys
} from "../../utils/pendo/useStandardizedTracking";
import {
  selectFilterList,
  selectFilters,
  selectSelectedView,
  selectSorting,
  selectTableColumns
} from "../../store/selector";
import { selectedViews } from "../../constant/table";
import { EXCLUDED_EXPORT_COLUMNS } from "../../constant/export";

/**
 * Bulk Action status values for tracking
 */
export const BulkActionStatus = Object.freeze({
  SUCCESS: "success",
  FAILURE: "failure",
  PARTIAL: "partial"
});

/**
 * Format sorting object into readable string array
 * @param {Object} sorting - Sorting configuration object
 * @returns {Array<string>} Formatted sort order array
 */
const formatSortOrder = (sorting) => {
  if (!sorting || Object.keys(sorting).length === 0) return [];

  return Object.entries(sorting)
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}:${value}`);
};

/**
 * Get visible columns as array of column names
 * @param {Object} tableColumns - Table columns configuration
 * @param {string} selectedView - Current selected view
 * @returns {Array<string>} Array of visible column names
 */
const getVisibleColumns = (tableColumns, selectedView) => {
  if (!tableColumns || !tableColumns[selectedView]) return [];

  return Object.keys(tableColumns[selectedView]).filter(
    (key) => tableColumns[selectedView][key]?.display && !EXCLUDED_EXPORT_COLUMNS.includes(key)
  );
};

/**
 * Get page key for the current view
 * @param {string} selectedView - Current selected view
 * @param {Object} filterList - Current filter list (saved list)
 * @returns {string} Page key for tracking
 */
const getPageKey = (selectedView, filterList) => {
  if (filterList?.id) {
    return PageKeys.SAVED_LISTS;
  }

  switch (selectedView) {
    case selectedViews.allPatients:
      return PageKeys.ALL_PATIENTS;
    case selectedViews.visitSchedule:
      return PageKeys.VISIT_SCHEDULE;
    case selectedViews.highPriority:
      return PageKeys.HIGH_PRIORITY;
    case selectedViews.savedList:
      return PageKeys.SAVED_LISTS;
    default:
      return PageKeys.ALL_PATIENTS;
  }
};

/**
 * Hook for tracking Pendo events related to Bulk Actions.
 * Provides tracking functions for all bulk action events with standardized additional_info.
 *
 * @returns {Object} Tracking functions and utilities
 *
 * @example
 * const { trackBulkActionViewed, trackBulkActionTriggered, trackBulkActionCompleted } = useBulkActionTracking();
 *
 * // Track when bulk action bar becomes visible
 * trackBulkActionViewed({ totalCountSelected: 5 });
 *
 * // Track when user confirms an action
 * trackBulkActionTriggered({ attribute: 'status', updateMode: 'Override', totalCountSelected: 5 });
 *
 * // Track when action completes
 * trackBulkActionCompleted({ attribute: 'status', updateMode: 'Override', totalCountSelected: 5, recordsSucceeded: 4, recordsFailed: 1, status: 'partial', runtimeMs: 1234 });
 */
export const useBulkActionTracking = () => {
  const { track, trackOnce } = useStandardizedTracking();

  // Redux selectors for additional_info
  const filterList = useSelector(selectFilterList);
  const filters = useSelector(selectFilters);
  const selectedView = useSelector(selectSelectedView);
  const sorting = useSelector(selectSorting);
  const tableColumns = useSelector(selectTableColumns);

  // Ref to track action start time for runtime calculation
  const actionStartTimeRef = useRef(null);

  /**
   * Build common additional_info object for bulk action events
   */
  const buildCommonAdditionalInfo = useCallback(
    (customInfo = {}) => {
      const isSavedList = !!filterList?.id;

      return {
        is_saved_list: isSavedList,
        saved_list_id: filterList?.id || null,
        saved_list_name: filterList?.name || null,
        saved_list_origin_bucket: filterList?.originBucket || null,
        total_count_available_in_list: filterList?.totalCount || null,
        sort_order: formatSortOrder(sorting),
        columns_visible: getVisibleColumns(tableColumns, selectedView),
        filters_applied: filters || {},
        ...customInfo
      };
    },
    [filterList, filters, sorting, tableColumns, selectedView]
  );

  /**
   * Get page key based on current view and saved list status
   */
  const pageKey = useMemo(() => getPageKey(selectedView, filterList), [selectedView, filterList]);

  /**
   * Track bulk_action_viewed event
   * Fired when the bulk action bar becomes visible (user selects records)
   *
   * @param {Object} options - Tracking options
   * @param {number} options.totalCountSelected - Number of records currently selected
   */
  const trackBulkActionViewed = useCallback(
    ({ totalCountSelected }) => {
      const additionalInfo = buildCommonAdditionalInfo({
        total_count_selected: totalCountSelected
      });

      track(EventNames.BULK_ACTION_VIEWED, {
        pageKey,
        featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
        feature: FeatureGroups.BULK_UPDATE,
        contentType: ContentTypes.PATIENT,
        contentKey: BulkActionContentKeys.BULK_ACTION_VIEWED,
        additionalInfo
      });
    },
    [track, pageKey, buildCommonAdditionalInfo]
  );

  /**
   * Track bulk_action_viewed once (prevents duplicate impression events)
   * Used for the initial view impression
   *
   * @param {string} key - Unique key for deduplication
   * @param {Object} options - Tracking options
   * @param {number} options.totalCountSelected - Number of records currently selected
   */
  const trackBulkActionViewedOnce = useCallback(
    (key, { totalCountSelected }) => {
      trackOnce(key, () => {
        trackBulkActionViewed({ totalCountSelected });
      });
    },
    [trackOnce, trackBulkActionViewed]
  );

  /**
   * Track bulk_action_triggered event
   * Fired when user confirms a bulk action (clicks confirm button)
   *
   * @param {Object} options - Tracking options
   * @param {string} options.attribute - Attribute being updated (status, assignee, facility, physician)
   * @param {string} options.updateMode - Update mode (Override, Add, Remove)
   * @param {number} options.totalCountSelected - Number of records being updated
   */
  const trackBulkActionTriggered = useCallback(
    ({ attribute, updateMode, totalCountSelected }) => {
      // Store start time for runtime calculation
      actionStartTimeRef.current = Date.now();

      const additionalInfo = buildCommonAdditionalInfo({
        total_count_selected: totalCountSelected,
        attribute,
        update_mode: updateMode
      });

      track(EventNames.BULK_ACTION_TRIGGERED, {
        pageKey,
        featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
        feature: FeatureGroups.BULK_UPDATE,
        contentType: ContentTypes.PATIENT,
        contentKey: BulkActionContentKeys.BULK_ACTION_TRIGGERED,
        additionalInfo
      });
    },
    [track, pageKey, buildCommonAdditionalInfo]
  );

  /**
   * Track bulk_action_completed event
   * Fired when a bulk action finishes (success, failure, or partial)
   *
   * @param {Object} options - Tracking options
   * @param {string} options.attribute - Attribute that was updated
   * @param {string} options.updateMode - Update mode used
   * @param {number} options.totalCountSelected - Total records that were selected
   * @param {number} options.recordsSucceeded - Number of records successfully updated
   * @param {number} options.recordsFailed - Number of records that failed
   * @param {string} options.status - Outcome status (success, failure, partial)
   * @param {number} [options.runtimeMs] - Runtime in milliseconds (auto-calculated if not provided)
   */
  const trackBulkActionCompleted = useCallback(
    ({ attribute, updateMode, totalCountSelected, recordsSucceeded, recordsFailed, status, runtimeMs }) => {
      // Calculate runtime if not provided
      const runtime = runtimeMs ?? (actionStartTimeRef.current ? Date.now() - actionStartTimeRef.current : null);

      const additionalInfo = buildCommonAdditionalInfo({
        total_count_selected: totalCountSelected,
        attribute,
        update_mode: updateMode,
        records_succeeded: recordsSucceeded,
        records_failed: recordsFailed,
        status,
        runtime_ms: runtime
      });

      track(EventNames.BULK_ACTION_COMPLETED, {
        pageKey,
        featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
        feature: FeatureGroups.BULK_UPDATE,
        contentType: ContentTypes.PATIENT,
        contentKey: BulkActionContentKeys.BULK_ACTION_COMPLETED,
        additionalInfo
      });

      // Reset start time
      actionStartTimeRef.current = null;
    },
    [track, pageKey, buildCommonAdditionalInfo]
  );

  /**
   * Track bulk_action_report_downloaded event
   * Fired when user clicks to download the CSV report
   *
   * @param {Object} options - Tracking options
   * @param {string} options.attribute - Attribute that was updated
   * @param {string} options.updateMode - Update mode used
   * @param {number} options.totalCountSelected - Total records that were selected
   * @param {number} options.recordsSucceeded - Number of records successfully updated
   * @param {number} options.recordsFailed - Number of records that failed
   * @param {string} options.status - Outcome status of the action
   * @param {boolean} options.reportDownload - Always true for this event
   */
  const trackBulkActionReportDownloaded = useCallback(
    ({ attribute, updateMode, totalCountSelected, recordsSucceeded, recordsFailed, status }) => {
      const additionalInfo = buildCommonAdditionalInfo({
        total_count_selected: totalCountSelected,
        attribute,
        update_mode: updateMode,
        records_succeeded: recordsSucceeded,
        records_failed: recordsFailed,
        status,
        report_download: true
      });

      track(EventNames.BULK_ACTION_REPORT_DOWNLOADED, {
        pageKey,
        featureGroup: FeatureGroups.PATIENT_LIST_MANAGEMENT,
        feature: FeatureGroups.BULK_UPDATE,
        contentType: ContentTypes.PATIENT,
        contentKey: BulkActionContentKeys.BULK_ACTION_REPORT_DOWNLOADED,
        additionalInfo
      });
    },
    [track, pageKey, buildCommonAdditionalInfo]
  );

  return {
    trackBulkActionViewed,
    trackBulkActionViewedOnce,
    trackBulkActionTriggered,
    trackBulkActionCompleted,
    trackBulkActionReportDownloaded,
    // Expose status constants
    BulkActionStatus
  };
};

export default useBulkActionTracking;
